import React, { useEffect, useState } from 'react';
import { Transaction } from '../../../api/types';
import formatCurrency from '../../../utils/formatCurrency';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCategoryBadgeColor } from '../utils/categoryBadge';

const parseLocalDateString = (value?: string | null): Date | null => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, (month || 1) - 1, day || 1);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

interface LedgerTableProps {
    transactions: Transaction[];
    isLoading: boolean;
    onRowClick: (id: string) => void;
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    hasActiveFilters?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    onSort?: (column: string) => void;
    selectedIds?: string[];
    onSelectionChange?: (ids: string[]) => void;
}

const LedgerTable: React.FC<LedgerTableProps> = ({
    transactions,
    isLoading,
    onRowClick,
    total,
    page,
    pageSize,
    onPageChange,
    hasActiveFilters = false,
    sortBy,
    sortOrder,
    onSort,
    selectedIds = [],
    onSelectionChange
}) => {
    const [pageInput, setPageInput] = useState(String(page));
    const [isEditingPage, setIsEditingPage] = useState(false);

    useEffect(() => {
        setPageInput(String(page));
        setIsEditingPage(false);
    }, [page]);


    const statusStyles: Record<string, string> = {
        needs_review: 'bg-red-100 text-red-800',
        verified: 'bg-green-100 text-green-800',
        awaiting_approval: 'bg-amber-100 text-amber-800',
        processing: 'bg-slate-100 text-slate-600',
    };

    const renderSortIcon = (column: string) => {
        if (sortBy !== column) return null;
        return (
            <span className="ml-1 inline-block">
                {sortOrder === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const handleHeaderClick = (column: string) => {
        if (onSort) {
            onSort(column);
        }
    };

    const getSortTooltip = (column: string) => {
        const isCurrentSort = sortBy === column;
        const orderLabels: Record<string, { asc: string, desc: string }> = {
            date: { asc: 'Earliest to Latest', desc: 'Latest to Earliest' },
            amount: { asc: 'Smallest to Largest', desc: 'Largest to Smallest' },
            vendor_name: { asc: 'A to Z', desc: 'Z to A' },
            fiscal_bucket: { asc: 'A to Z', desc: 'Z to A' },
            resolution_status: { asc: 'A to Z', desc: 'Z to A' }
        };

        const labels = orderLabels[column] || { asc: 'Ascending', desc: 'Descending' };

        if (!isCurrentSort) {
            return `${labels.asc}`;
        }

        const nextLabel = sortOrder === 'asc' ? labels.desc : labels.asc;
        return `${nextLabel}`;
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onSelectionChange) {
            if (e.target.checked) {
                // Select all visible transactions
                const allIds = transactions.map(t => t._id);
                onSelectionChange(allIds);
            } else {
                // Deselect all
                onSelectionChange([]);
            }
        }
    };

    const handleSelectRow = (id: string, checked: boolean) => {
        if (onSelectionChange) {
            if (checked) {
                onSelectionChange([...selectedIds, id]);
            } else {
                onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
            }
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center text-gray-500">Loading transactions...</div>;
    }

    const totalPages = Math.ceil(total / pageSize);
    const pageStart = total > 0 ? (page - 1) * pageSize + 1 : 0;
    const pageEnd = total > 0 ? Math.min(page * pageSize, total) : 0;
    const allSelected = transactions.length > 0 && transactions.every(t => selectedIds.includes(t._id));
    const someSelected = transactions.some(t => selectedIds.includes(t._id));
    const handlePageSubmit = () => {
        const parsed = Number(pageInput);
        if (!Number.isFinite(parsed)) {
            setPageInput(String(page));
            setIsEditingPage(false);
            return;
        }
        const nextPage = Math.min(Math.max(Math.trunc(parsed), 1), Math.max(totalPages, 1));
        setPageInput(String(nextPage));
        setIsEditingPage(false);
        if (nextPage !== page) onPageChange(nextPage);
    };

    return (
        <div className="w-full relative min-h-0 flex flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-auto">
                <table className="w-full text-left border-collapse table-fixed min-w-[1100px] [&_th]:font-inter [&_th]:font-medium [&_th]:text-[14px] [&_th]:leading-[120%] [&_th]:tracking-normal [&_th]:align-middle">
                    <thead className="border-b border-gray-200 text-[#4C545F] sticky top-0 z-20 bg-white">
                        <tr className="border-b border-gray-200">
                            {onSelectionChange && (
                                <th className="pl-6 pr-0 py-4 w-12">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300"
                                        checked={allSelected}
                                        ref={input => {
                                            if (input) {
                                                input.indeterminate = someSelected && !allSelected;
                                            }
                                        }}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                            )}
                            <th
                                className="group relative p-4 w-[13%] cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleHeaderClick('date')}
                            >
                                Date {renderSortIcon('date')}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1.5 rounded whitespace-nowrap shadow-xl border border-gray-700 pointer-events-none group-hover:opacity-100 z-[110]">
                                    {getSortTooltip('date')}
                                </div>
                            </th>
                            <th
                                className="group relative p-4 w-[20%] cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleHeaderClick('vendor_name')}
                            >
                                Vendor {renderSortIcon('vendor_name')}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1.5 rounded whitespace-nowrap shadow-xl border border-gray-700 pointer-events-none group-hover:opacity-100 z-[110]">
                                    {getSortTooltip('vendor_name')}
                                </div>
                            </th>
                            <th className="p-4 w-[19%]">Description</th>
                            <th
                                className="group relative p-4 pr-8 w-[14%] text-right cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleHeaderClick('amount')}
                            >
                                Amount {renderSortIcon('amount')}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1.5 rounded whitespace-nowrap shadow-xl border border-gray-700 pointer-events-none group-hover:opacity-100 z-[110]">
                                    {getSortTooltip('amount')}
                                </div>
                            </th>
                            <th
                                className="group relative p-4 pl-4 w-[18%] cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleHeaderClick('fiscal_bucket')}
                            >
                                Finz Category {renderSortIcon('fiscal_bucket')}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1.5 rounded whitespace-nowrap shadow-xl border border-gray-700 pointer-events-none group-hover:opacity-100 z-[110]">
                                    {getSortTooltip('fiscal_bucket')}
                                </div>
                            </th>
                            <th
                                className="group relative p-4 pr-6 w-[16%] cursor-pointer hover:bg-gray-50 select-none bg-white"
                                onClick={() => handleHeaderClick('resolution_status')}
                            >
                                Status {renderSortIcon('resolution_status')}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 opacity-0 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1.5 rounded whitespace-nowrap shadow-xl border border-gray-700 pointer-events-none group-hover:opacity-100 z-[110]">
                                    {getSortTooltip('resolution_status')}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {transactions.map((txn, index) => (
                            <tr
                                key={txn._id}
                                data-tutorial={index === 0 ? 'margin-drilldown-first-row' : undefined}
                                onClick={() => onRowClick(txn._id)}
                                className="hover:bg-gray-50 cursor-pointer transition-colors group"
                            >
                                {onSelectionChange && (
                                    <td className="pl-6 pr-8 py-4" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300"
                                            checked={selectedIds.includes(txn._id)}
                                            onChange={(e) => handleSelectRow(txn._id, e.target.checked)}
                                        />
                                    </td>
                                )}
                                <td className="p-4 whitespace-nowrap text-[14px] font-medium text-gray-500 truncate overflow-hidden">
                                    {parseLocalDateString(txn.date)?.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) || '—'}
                                </td>
                                <td className="p-4 text-[14px] text-gray-900 truncate overflow-hidden group-hover:text-indigo-600 transition-colors" title={txn.vendor_name || 'Unknown Vendor'}>{txn.vendor_name || 'Unknown Vendor'}</td>
                                <td className="p-4 text-[14px] text-gray-500 truncate overflow-hidden" title={txn.original_description}>{txn.original_description}</td>
                                <td className={`p-4 pr-8 text-[14px] font-medium text-right truncate overflow-hidden ${txn.amount > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                                    {txn.amount > 0 ? '+' : ''}{formatCurrency(txn.amount).replace('$', '')}
                                </td>
                                <td className="p-4 pl-4 text-[14px] truncate overflow-hidden">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(txn.fiscal_bucket)}`}>
                                        <span className="capitalize">
                                            {txn.fiscal_bucket || 'Uncategorized'}
                                        </span>
                                    </span>
                                </td>
                                <td className="p-4 pr-6 text-[14px] bg-white">
                                    <div className="flex justify-start">
                                        <span data-tutorial={index === 0 ? 'margin-drilldown-status' : undefined} className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${(txn.resolution_status && statusStyles[txn.resolution_status]) || statusStyles.processing
                                            }`}>
                                            <span className="capitalize">
                                                {txn.resolution_status?.replace(/_/g, ' ') || 'Processing'}
                                            </span>
                                        </span>
                                    </div>
                                </td>


                            </tr>
                        ))}
                        {transactions.length === 0 && (
                            <tr>
                                <td colSpan={8} className="p-12 text-center text-gray-500">
                                    {!hasActiveFilters ? (
                                        <div className="flex items-center justify-center gap-1">
                                            <p className="text-gray-500 font-medium text-sm">AI is still processing your data</p>
                                            <div className="flex space-x-1 pt-2">
                                                <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                                <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                                <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"></div>
                                            </div>
                                        </div>
                                    ) : (
                                        "No transactions found matching your filters."
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer Pagination - mt-auto forces it to the bottom of the flex container */}
            <div className="sticky bottom-0 z-10 mt-auto flex flex-shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-6">

                {/* 1. LEFT SIDE: Results count (Pinned to left) */}
                <div className="min-w-0 flex flex-1 justify-start">
                    <span className="truncate text-xs font-medium text-gray-500">
                        {pageStart} - {pageEnd} of {String(total).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} results
                    </span>
                </div>

                {/* 2. CENTER: Pagination Controls */}
                <div className="flex flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap sm:gap-4">
                    <button
                        disabled={page === 1}
                        onClick={() => onPageChange(page - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-[#F9FAFB] text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-6 sm:w-8"
                    >
                        <ChevronLeft size={16} />
                    </button>

                    <span className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        Page{' '}
                        {isEditingPage ? (
                            <input
                                type="number"
                                min={1}
                                max={Math.max(totalPages, 1)}
                                value={pageInput}
                                autoFocus
                                onChange={(e) => setPageInput(e.target.value)}
                                onBlur={handlePageSubmit}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handlePageSubmit();
                                    if (e.key === 'Escape') {
                                        setPageInput(String(page));
                                        setIsEditingPage(false);
                                    }
                                }}
                                className="mx-1 h-6 w-10 border-0 border-b-2 border-gray-400 bg-transparent px-0 text-center text-sm text-gray-600 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                aria-label="Current page"
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsEditingPage(true)}
                                className="mx-1 min-w-[1.25rem] border-b-2 border-gray-300 px-0.5 text-center text-sm text-gray-600 transition-colors hover:border-gray-500"
                                aria-label={`Edit page number, current page ${page}`}
                                title="Click to jump to a page"
                            >
                                {page}
                            </button>
                        )}{' '}
                        of {totalPages || 1}
                    </span>



                    <button
                        disabled={page === totalPages || totalPages === 0}
                        onClick={() => onPageChange(page + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-indigo-100 bg-[#F0F0FF] text-indigo-600 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-6 sm:w-8"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>

                {/* 3. RIGHT SIDE: Empty spacer to balance the flexbox */}
                <div className="hidden flex-1 sm:block" />
            </div>
        </div>
    );
};

export default LedgerTable;
